import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetActivityLogsQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import { useLanguage } from '../../../context/LanguageContext';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate().toString().padStart(2,'0')} ${months[d.getMonth()]}, ${d.getFullYear()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
};

const actionBadge = (action: string) => {
  const map: Record<string, string> = {
    create: 'badge-light-success',
    update: 'badge-light-primary',
    delete: 'badge-light-danger',
    status_update: 'badge-light-warning',
  };
  return map[action] || 'badge-light-secondary';
};

const AdminActivityLogs: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [module, setModule]       = useState(searchParams.get('module') || '');
  const [action, setAction]       = useState(searchParams.get('action') || '');
  const [userId, setUserId]       = useState(searchParams.get('user_id') || '');
  const [daterange, setDaterange] = useState(searchParams.get('daterange') || '');
  const [search, setSearch]       = useState(searchParams.get('search') || '');
  const [page, setPage]           = useState(Number(searchParams.get('page')) || 1);

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const params: Record<string, any> = { page };
  if (module)    params.module    = module;
  if (action)    params.action    = action;
  if (userId)    params.user_id   = userId;
  if (daterange) params.daterange = daterange;
  if (search)    params.search    = search;

  const { data, isLoading, error } = useGetActivityLogsQuery(params);

  // Sync state → URL params
  useEffect(() => {
    const sp: Record<string, string> = {};
    if (module)       sp.module    = module;
    if (action)       sp.action    = action;
    if (userId)       sp.user_id   = userId;
    if (daterange)    sp.daterange = daterange;
    if (search)       sp.search    = search;
    if (page > 1)     sp.page      = String(page);
    setSearchParams(sp, { replace: true });
  }, [module, action, userId, daterange, search, page]);

  const resetPage = () => setPage(1);

  const clearAll = () => {
    setModule(''); setAction(''); setUserId('');
    setDaterange(''); setSearch(''); setPage(1);
  };

  const hasFilters = module || action || userId || daterange || search;

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">{t('Error loading activity logs')}</div>;

  const logs    = data?.data?.data || [];
  const meta    = data?.data || { current_page: 1, last_page: 1, total: 0 };
  const modules = data?.modules || [];
  const admins  = data?.admins  || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Activity Logs')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">{t('Home')}</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Activity Logs')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Content */}
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6 flex-wrap gap-3">
                {/* Search */}
                <div className="card-title">
                  <div className="d-flex align-items-center position-relative my-1">
                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor" />
                        <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-solid w-250px ps-15"
                      placeholder={t('Search user or module...')}
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                    />
                  </div>
                </div>

                {/* Filters */}
                <div className="card-toolbar d-flex align-items-center gap-2 flex-wrap">
                  {/* Module filter */}
                  <select
                    className="form-select form-select-solid w-150px"
                    value={module}
                    onChange={(e) => { setModule(e.target.value); resetPage(); }}
                  >
                    <option value="">{t('All Modules')}</option>
                    {modules.map((m: string) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>

                  {/* Action filter */}
                  <select
                    className="form-select form-select-solid w-150px"
                    value={action}
                    onChange={(e) => { setAction(e.target.value); resetPage(); }}
                  >
                    <option value="">{t('All Actions')}</option>
                    <option value="create">{t('Create')}</option>
                    <option value="update">{t('Update')}</option>
                    <option value="delete">{t('Delete')}</option>
                    <option value="status_update">{t('Status Update')}</option>
                  </select>

                  {/* Admin user filter */}
                  <select
                    className="form-select form-select-solid w-180px"
                    value={userId}
                    onChange={(e) => { setUserId(e.target.value); resetPage(); }}
                  >
                    <option value="">{t('All Users')}</option>
                    {admins.map((a: any) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>

                  {/* Date range */}
                  <input
                    type="text"
                    className="form-control form-control-solid w-220px"
                    placeholder={t('Date range (YYYY-MM-DD - YYYY-MM-DD)')}
                    value={daterange}
                    onChange={(e) => { setDaterange(e.target.value); resetPage(); }}
                  />

                  {hasFilters && (
                    <button className="btn btn-light-danger btn-sm" onClick={clearAll}>
                      {t('Clear Filters')}
                    </button>
                  )}
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <span className="text-muted fs-7">
                    {t('Total:')} <strong>{meta.total || 0}</strong> {t('records')}
                  </span>
                </div>

                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                        <th className="min-w-40px">#</th>
                        <th className="min-w-100px">{t('Module')}</th>
                        <th className="min-w-80px">{t('Action')}</th>
                        <th className="min-w-120px">{t('Performed By')}</th>
                        <th className="min-w-80px">{t('Ref ID')}</th>
                        <th className="min-w-120px">{t('Date')}</th>
                        <th className="min-w-80px">{t('Details')}</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 fw-semibold">
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-10">{t('No activity logs found')}</td>
                        </tr>
                      ) : (
                        logs.map((log: any) => (
                          <React.Fragment key={log.id}>
                            <tr>
                              <td>{log.id}</td>
                              <td>
                                <span className="badge badge-light-info">{log.module}</span>
                              </td>
                              <td>
                                <span className={`badge ${actionBadge(log.action)}`}>
                                  {log.action.replace('_', ' ')}
                                </span>
                              </td>
                              <td>{log.user_name || '-'}</td>
                              <td>{log.reference_id || '-'}</td>
                              <td>{formatDate(log.created_at)}</td>
                              <td>
                                {(log.old_data || log.new_data) && (
                                  <button
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                                  >
                                    {expandedRow === log.id ? t('Hide') : t('View')}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedRow === log.id && (
                              <tr>
                                <td colSpan={7} className="bg-light-secondary">
                                  <div className="row g-4 p-4">
                                    {log.old_data && (
                                      <div className="col-md-6">
                                        <h6 className="text-danger mb-2">{t('Before')}</h6>
                                        <pre className="bg-white rounded p-3 fs-8 text-wrap" style={{ maxHeight: 250, overflowY: 'auto' }}>
                                          {JSON.stringify(log.old_data, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    {log.new_data && (
                                      <div className="col-md-6">
                                        <h6 className="text-success mb-2">{t('After')}</h6>
                                        <pre className="bg-white rounded p-3 fs-8 text-wrap" style={{ maxHeight: 250, overflowY: 'auto' }}>
                                          {JSON.stringify(log.new_data, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={meta.current_page}
                  lastPage={meta.last_page}
                  total={meta.total || 0}
                  onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminActivityLogs;
